// Standard reader pipeline factory.
//
// Composes the markdown-it plugin chain shared by the SPA reader and the
// share-page renderer:
//
//   frontmatter (always strips ---…--- from the body; raw YAML to callback)
//   anchor       (heading IDs; optional permalink anchor)
//   callouts     (GitHub callout blockquotes — see ./callouts.ts)
//   wikilink     (configurable mode — see ./wikilink.ts)
//   task-lists   (read-only checkboxes via `enabled: false`)
//   deflist
//   footnote
//   mark
//   emoji        (full set)
//
// Each consumer wraps the returned MarkdownIt instance with their own
// rendering concerns:
//   - SPA reader: Shiki-driven highlight callback; permalink anchors;
//     wikilink: 'plain' (current behavior).
//   - Share renderer: plain code blocks; no permalink; wikilink: 'inert';
//     plus its own link/image hardening renderer-rule overrides applied
//     on top.

import MarkdownIt from 'markdown-it';
import frontMatter from 'markdown-it-front-matter';
import anchor from 'markdown-it-anchor';
import taskLists from 'markdown-it-task-lists';
import deflist from 'markdown-it-deflist';
import footnote from 'markdown-it-footnote';
import mark from 'markdown-it-mark';
import { full as emoji } from 'markdown-it-emoji';

import { calloutsPlugin } from './callouts.js';
import { wikilinkPlugin } from './wikilink.js';

export interface BuildReaderMdOptions {
  /** Fenced-code highlighter. Default: plain <pre><code class="language-X">. */
  highlight?: (code: string, lang: string) => string;
  /** markdown-it-anchor permalink config.
   *  - false (default): heading IDs only, no permalink anchor.
   *  - true:            install anchor.permalink.linkInsideHeader with the
   *                     SPA's existing shape.
   *  - object:          passed through verbatim. */
  permalink?: boolean | object;
  /** Wikilink mode. Default: 'inert'. See WikilinkPluginOptions. */
  wikilink?: 'inert' | 'extraction' | 'plain';
  /** Frontmatter raw-YAML callback. Frontmatter is always stripped from
   *  the rendered body; pass this to capture the raw YAML string. */
  onFrontmatter?: (raw: string) => void;
  /** When true, stamp `data-line="<1-based source line>"` on block-level
   *  open tokens so the SPA reader can scroll to a grep hit's line. Default
   *  false — other consumers (share renderer) get unchanged HTML. */
  sourceLines?: boolean;
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function plainHighlight(code: string, lang: string): string {
  const langClass = lang ? ` class="language-${escapeHtml(lang)}"` : '';
  return `<pre><code${langClass}>${escapeHtml(code)}</code></pre>`;
}

function slugify(text: string): string {
  return String(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function makeUniqueSlug(): (text: string) => string {
  const seen = new Map<string, number>();
  return (text: string) => {
    const base = slugify(text) || 'section';
    if (!seen.has(base)) {
      seen.set(base, 2);
      return base;
    }
    const n = seen.get(base)!;
    seen.set(base, n + 1);
    return `${base}-${n}`;
  };
}

function resolvePermalink(p: boolean | object | undefined): object | false {
  if (p === true) {
    return anchor.permalink.linkInsideHeader({
      symbol: '',
      ariaHidden: false,
      class: 'heading-anchor',
    });
  }
  if (p && typeof p === 'object') return p;
  return false;
}

export function buildReaderMd(opts: BuildReaderMdOptions = {}): MarkdownIt {
  const highlight = opts.highlight ?? plainHighlight;

  const md = new MarkdownIt({
    html: false,
    linkify: true,
    typographer: false,
    breaks: false,
    highlight,
  });

  md.use(frontMatter, opts.onFrontmatter ?? (() => { /* discard */ }));

  const unique = makeUniqueSlug();
  md.use(anchor, {
    slugify: (text: string) => unique(text),
    permalink: resolvePermalink(opts.permalink),
  });

  md.use(calloutsPlugin);
  md.use(wikilinkPlugin, { mode: opts.wikilink ?? 'inert' });
  md.use(taskLists, { enabled: false, label: false });
  md.use(deflist);
  md.use(footnote);
  md.use(mark);
  md.use(emoji);

  if (opts.sourceLines === true) {
    md.core.ruler.push('mindex_source_lines', (state) => {
      for (const tok of state.tokens) {
        if (tok.map && tok.type.endsWith('_open')) {
          tok.attrSet('data-line', String(tok.map[0] + 1)); // map is 0-based
        }
      }
    });
  }

  return md;
}
